import React, {useEffect, useState} from 'react';
import PaginationOptions, {Page} from '../../../../api/ui/paginationOptions';
import useTranslation from '../../../../hooks/useTranslation';
import {Helmet} from 'react-helmet';
import {useDispatch} from 'react-redux';
import {DEFAULT_END_AT, DEFAULT_START_AT} from '../../constants';
import Visible from '../../../../components/Custom/Visible';
import PaginationComponent from '../../../../components/Custom/PaginationComponent';
import BOTable from '../../../../components/Tables/BOTable';
import Notification from '../../../../components/Notification/Notification';
import {transactionAttemptLogListRequest} from "../../../../redux/actions/log/transactionAttemptLog";
import useSchemaTransactionAttemptLog from "../../../../schema/table-schema/useSchemaTransactionAttemptLog";
import {UIConstants} from "../../../../constants/UIConstants";
import {
    SearchBtnContainer,
    SearchContainer,
    SearchContent,
    SearchDateRange,
    SearchLabel,
    SearchRow
} from "../../../../components/Search/searchSection";

function TransactionAttemptLogMain() {
    // -- component const --//
    const title = "admin.log.user.transactionAttempt.title" // "거래 시도 로그"

    // 페이지 옵션 키
    const OPTION_KEY = Page.LOG_USER_WALLET_CONNECTION;

    const {t} = useTranslation();
    const {schema} = useSchemaTransactionAttemptLog();

    // -- useSelector & dispatch --//
    const dispatch = useDispatch();

    // -- state --//
    const [tBody, setTbody] = useState();
    const [totalCount, setTotalCount] = useState(0);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [showNotification, setShowNotification] = useState(false);

    // 페이지 옵션
    const [pageOption, setPageOption] = useState();
    const [pageLength, setPageLength] = useState(25);
    const [page, setPage] = useState(1);

    const [startAt, setStartAt] = useState(DEFAULT_START_AT);
    const [endAt, setEndAt] = useState(DEFAULT_END_AT);

    // 로딩
    const [isLoading, setIsLoading] = useState(false);

    // 검색
    const [accountNo, setAccountNo] = useState(0);
    const [walletAddress, setWalletAddress] = useState('');
    const [isProcessed, setIsProcessed] = useState(0);

    const [isReset, setIsReset] = useState(false);

    // -- func & handler --//
    const fetchList = () => {
        setIsLoading(true);

        dispatch(
            transactionAttemptLogListRequest({
                page: page,
                pageLength: pageLength,
                beginDate: startAt,
                endDate: endAt,
                accountNo: accountNo,
                walletAddress: walletAddress,
                status: isProcessed,
                callback: (data) => {
                    console.log('요청 결과: ', data);
                    setIsLoading(false);

                    // 인덱스를 추가하여 새로운 배열을 생성(중복 키 방지)
                    const listWithIndex = data?.list.map((item, index) => ({
                        index: index + 1, // 인덱스는 1부터 시작(번호 표기시 편의를 위해)
                        ...item,
                    }));

                    setTbody(listWithIndex);

                    setTotalCount(data.totalCount);
                },
                error: (e) => {
                    console.log(e);
                    setNotificationMessage(e.toString());
                    setShowNotification(true);
                    setIsLoading(false);
                },
            }),
        );
    };

    const handlePageChange = (_, value) => {
        setPage(Number(value));
    };

    const handlePageLengthChange = (_, value) => {
        setPageLength(Number(value));
        setPage(1);
    };

    const handleSearchClick = () => {
        if (page === 1) {
            fetchList();
            return;
        }

        setPage(1);
    };

    const handleResetClick = () => {
        setAccountNo(0);
        setWalletAddress("");
        setIsProcessed(0);
        setIsReset(true);
    }

    // -- react hooks --//
    useEffect(() => {
        PaginationOptions.getInstance().then((instance) => {
            const option = instance?.getData(OPTION_KEY);
            setPageOption(option);

            if (option.list_count_elements?.length > 0) {
                setPageLength(Number(25));
            }
        });
    }, [OPTION_KEY]);

    useEffect(() => {
        fetchList();
    }, [page, pageLength]);

    useEffect(() => {
      if (isReset) {
        setIsReset(false);
        fetchList();
      }
    }, [isReset]);

    // -- render --//
    return (
        <div className='page'>
            <Helmet>
                <title>{t(title)}</title>
            </Helmet>

            <div className="topSection">
                <div className="leftSection">
                    <p className="titleText">{t(title)}</p>
                </div>
            </div>

            <div className="spaceDiv10"/>

            <section>
                <SearchContainer>
                    <SearchRow>
                        <SearchLabel text={UIConstants.Date}/>
                        <SearchDateRange
                            startAt={startAt}
                            endAt={endAt}
                            setStartAt={setStartAt}
                            setEndAt={setEndAt}
                        />
                    </SearchRow>

                    <SearchRow>
                        <SearchLabel text={UIConstants.UserId}/>
                        <SearchContent>
                            <input
                                className=""
                                value={accountNo === 0 ? '' : accountNo}
                                placeholder={UIConstants.UserId}
                                onChange={(e) => {
                                    setAccountNo(Number(e.target.value));
                                }}
                            />
                        </SearchContent>
                        <SearchLabel text={UIConstants.WalletNo}/>
                        <SearchContent>
                            <input
                                className="address"
                                value={walletAddress ?? ''}
                                placeholder="온체인 지갑 주소"
                                onChange={(e) => {
                                    setWalletAddress(e.target.value);
                                }}
                            />
                        </SearchContent>
                    </SearchRow>
                </SearchContainer>

                <SearchBtnContainer
                    handleSearchClick={handleSearchClick}
                    handleResetClick={handleResetClick}
                />
            </section>

            <div className="spaceDiv10"/>

            <Visible Visible visible={pageOption?.top_paging}>
                <PaginationComponent
                    disable={false}
                    option={pageOption}
                    page={page}
                    pageLength={pageLength}
                    totalCount={totalCount}
                    onChange={handlePageChange}
                    onSelect={handlePageLengthChange}
                />

                <div className="spaceDiv10"/>
            </Visible>

            <div className="spaceDiv10"/>

            <BOTable
                schema={schema}
                rows={tBody}
                loading={isLoading}
                onClick={null}
            />

            <div className="spaceDiv10"/>

            <Visible Visible visible={pageOption?.bottom_paging}>
                <PaginationComponent
                    disable={false}
                    option={pageOption}
                    page={page}
                    pageLength={pageLength}
                    totalCount={totalCount}
                    onChange={handlePageChange}
                    onSelect={handlePageLengthChange}
                />
            </Visible>

            {showNotification ? (
                <Notification
                    close={() => setShowNotification(false)}
                    message={notificationMessage}
                />
            ) : null}

        </div>
    );
}

export default TransactionAttemptLogMain;
